<template>
  <div class="container">
    <div v-if="isEdit">
      <div class="columns">
        <input v-model="conditions.id" value="" type="hidden" />
        <div class="column is-4">
          <b-field label="Travel Attribute">
            <b-select
              v-model="selectedAttribute"
              required
              placeholder="Select Travel Attribute"
              @input="handleSelectedAttributeChange"
            >
              <option value="">--Select Travel Attribute--</option>
              <option v-for="item in getTravelAttributes" :key="item.id" :value="item.id">
                {{ item.name }}
              </option>
            </b-select>
          </b-field>
        </div>
        <div v-if="selectedAttribute" class="column is-3">
          <b-field label="Comparison Operator">
            <b-select v-model="conditions.comparison.operator" required placeholder="Select Comparison Operator">
              <option v-for="(item, key) in selectedTravelAttributeDetails.operators.split(';')" :key="key" :value="item">
                {{ $helpers.getComparisionLabel(item).toUpperCase() }}
              </option>
            </b-select>
          </b-field>
        </div>
        <div v-if="selectedAttribute" class="column is-3">
          <b-field v-if="selectedTravelAttributeDetails.type === 'Text'" label="Comparison Value">
            <b-input v-if="isCustomValue" v-model="conditions.comparison.value" placeholder="Comparison Value" />
            <TagAutocomplete
              v-else
              :onlyOne="!isList"
              :chainData="tagOptions"
              :key="tagAutocompleteKey"
              @list="values => setComparisonValue(values)"
            />
          </b-field>
          <span v-if="isList && isCustomValue" class="help is-info">Enter comma separated list of values.</span>
          <b-field v-if="isDurationField" label="Comparison Value">
            <div class="columns">
              <b-numberinput v-model="conditions.comparison.value1" :controls="false" placeholder="Hours" class="column" />&nbsp;
              <b-numberinput v-model="conditions.comparison.value2" required :controls="false" placeholder="Minutes" class="column" />
            </div>
            <b-input v-model="conditions.comparison.value" type="hidden" />
          </b-field>
          <b-field v-if="isNumberField" label="Comparison Value">
            <b-numberinput v-model="conditions.comparison.value" :controls="false" placeholder="Enter Numeric value" />
          </b-field>
          <b-field v-if="selectedTravelAttributeDetails.type === 'Date'" label="Comparison Value">
            <b-datepicker
              trap-focus
              locale="en-US"
              :value="valueAsDate"
              icon="calendar-today"
              position="is-bottom-left"
              placeholder="Select a date..."
              @input="valueChanged($event)"
            />
          </b-field>
        </div>
        <div v-if="selectedAttribute" class="column is-2">
          <b-field v-if="selectedTravelAttributeDetails.units && selectedTravelAttributeDetails.units !== 'car_size'" label="Comparison Units">
            <b-select v-model="conditions.comparison.valueUnits" required placeholder="Select Comparison Units">
              <option
                v-for="(item, key) in selectedTravelAttributeDetails.units.split(';')"
                :key="key"
                :value="item"
                :selected="key === 0"
              >
                {{ item | capitalize }}
              </option>
            </b-select>
          </b-field>
        </div>
      </div>
      <div>
        <span>
          <b-button @click="remove">Cancel</b-button>&nbsp;
          <b-button type="is-success" :disabled="!isFormValid" @click="save">Save Condition</b-button>
        </span>
      </div>
    </div>
    <div v-else class="columns">
      <div class="column is-10">{{ conditions.message }}</div>
      <div class="column is-1">
        <a @click="isEdit = !isEdit">Edit</a>
      </div>
      <div class="column is-1">
        <a v-if="conditions.id" @click="remove(conditions.id)">
          Delete
        </a>
        <a v-else @click="remove(conditions.uid)">Delete</a>
      </div>
    </div>
  </div>
</template>

<script src="https://cdnjs.cloudflare.com/ajax/libs/moment.js/2.27.0/moment.min.js" />

<script>
import TagAutocomplete from '@/components/Tags/TagAutocomplete.vue';
const moment = require('moment');

export default {
  components: { TagAutocomplete },
  props: {
    mode: String,
    type: String,
    index: Number,
    condition: {
      type: [Object],
    },
  },
  data() {
    return {
      isEdit: false,
      loading: false,
      allowComparison: true,
      tagAutocompleteKey: 0,
      conditions: this.condition,
      travelPolicyAttributes: this.$store.state.travelPolicy.travelPolicyAttributes,
      selectedAttribute: this.condition.attribute.id ? this.condition.attribute.id : '',
    };
  },
  computed: {
    isCustomValue() {
      const value = this.getTravelAttributes.find(a => a.id === this.selectedAttribute);
      const predefinedValueNames = [
        'Air - Cabin',
        'Air - Airport Code',
        'Air - Provider Code',
        'Air - Origin Country',
        'Air - Origin Airport Code',
        'Air - Destination Country',
        'Air - Layover Airport Code',
        'Air - Destination Airport Code',
        'Car - Size',
        'Car - Country Code',
        'Car - Provider Code',
        'Car - Pick Up Country Code',
        'Car - Drop Off Country Code',
        'Hotel - City',
        'Hotel - Country',
        'Hotel - Property Code',
        'Hotel - Provider Code',
        'Hotel - Guarantee Type',
      ];

      return !predefinedValueNames.includes(value.name);
    },
    tagOptions() {
      const airports = this.$globals.airports;
      const airlineNames = this.$globals.airlineNames;
      const hotelChains = this.$globals.vendors.hotelChains;
      const carProviders = this.$globals.vendors.carProviders;
      const value = this.getTravelAttributes.find(a => a.id === this.selectedAttribute);

      switch (value.name) {
        case 'Car - Size':
          this.allowComparison = false; 
          return Object.entries(this.$store.state.options.carSizes);
          break;
        case 'Air - Cabin': // check
          return Object.entries(this.$store.state.options.airCabins);
          break;
        case 'Hotel - Country':
        case 'Air - Origin Country':
        case 'Air - Destination Country':
          // return countries.map(country => [country, country]);
          return Object.entries(this.$store.state.options.countries);
          break;
        case 'Hotel - Guarantee Type':
          return Object.entries(this.$store.state.options.hotelGuaranteeTypes);
          break;
        case 'Hotel - Provider Code':
          return Object.keys(hotelChains)
            .map(hotelChain => [hotelChain, hotelChain]);
          break;
        case 'Air - Provider Code': // check
          return Object.keys(airlineNames)
            .map(airlineName => [
              airlineName,
              airlineNames[airlineName],
              [airlineName, airlineNames[airlineName]],
            ]);
          break;
        case 'Car - Provider Code':
          return Object.keys(carProviders)
            .map(p => [p, carProviders[p], [p, carProviders[p]]]);
          break;
        case 'Air - Airport Code': // check
        case 'Air - Origin Airport Code': // check
        case 'Air - Layover Airport Code': // check
        case 'Air - Destination Airport Code': //check
          return [...new Set(airports)]
            .map(airport => [
              airport.code,
              `${airport.code} - ${airport.city} ${airport.state ? ', ' + airport.state : ''}`,
              airport,
            ]);
          break;
        case 'Hotel - City':
          return Object.entries(this.$store.state.options.cities);
          break;
        case 'Car - Country Code':
        case 'Car - Pick Up Country Code':
        case 'Car - Drop Off Country Code':
          return Object.entries(this.$store.state.options.countries);
          break;
        default:
          return [];
      }
    },
    selectedTravelAttributeDetails() {
      if (this.selectedAttribute) {
        return this.travelPolicyAttributes.find(attr => {
          return attr.id === this.selectedAttribute;
        });
      }

      return null;
    },
    isList() {
      return (
        this.conditions.comparison.operator === 'in' ||
        this.conditions.comparison.operator === 'not_in'
      );
    },
    transformOperator() {
      let operatorLabel;

      switch (this.conditions.comparison.operator) {
        case 'less_than':
          operatorLabel = 'less than';
          break;
        case 'greater_than':
          operatorLabel = 'greater than';
          break;
        case 'equal':
        case 'in':
          operatorLabel = 'equal to';
          break;
        case 'not_equal':
          operatorLabel = 'not equal to';
          break;
        case 'less_than_inclusive':
          operatorLabel = '<=';
          break;
        case 'greater_than_inclusive':
          operatorLabel = 'equal to or more than';
          break;
        case 'in':
          operatorLabel = 'equal to';
          break;
        case 'not_in':
          operatorLabel = 'not equal to';
          break;
        case 'true':
          operatorLabel = 'is TRUE';
          break;
        case 'false':
          operatorLabel = 'is FALSE';
          break;
        default:
          operatorLabel = '';
      }

      return operatorLabel;
    },
    transformValue() {
      const value = this.conditions?.comparison?.value || '';
      const valueUnits = this.conditions?.comparison?.valueUnits || '';
      let displayValue = `${value} ${valueUnits}`;

      switch (valueUnits) {
        case 'dollars':
          displayValue = `$${value}`;
          break;
        case 'days':
          displayValue = value;
          break;
        case 'minutes':
          const hours = this.conditions.comparison.value1;
          const mins = this.conditions.comparison.value2;
          displayValue = `${hours} ${hours === 1 ? ' hour ' : ' hours '} ${mins} mins`;
          break;
        case '':
          displayValue = value;
          break;
      }

      return displayValue;
    },
    isFormValid() {
      
      const isComparisionUnitsFilled = this.conditions.comparison.valueUnits
      const isComparisionOperatorFilled = this.conditions.comparison.operator
      const isComparisionValueFilled = this.isDurationField ? this.conditions.comparison.value2 !== null : this.conditions.comparison.value !== null;


      if (this.selectedAttribute) {
        if (this.selectedTravelAttributeDetails.type === 'Boolean') {
          return isComparisionOperatorFilled;
        } else {
          if (this.selectedTravelAttributeDetails.units) {
            return isComparisionOperatorFilled && isComparisionValueFilled && isComparisionUnitsFilled;
          } else {
            return isComparisionOperatorFilled && isComparisionValueFilled;
          }
        }
      }

      return false;
    },
    isDurationField() {
      if (this.selectedAttribute) {
          const attr = [
          'Air - In Flight Duration',
          'Air - Journey Duration',
          'Air - Layover Duration',
        ];

        return attr.includes(this.selectedTravelAttributeDetails.name);
      }

      return false;
    },
    isNumberField() {
      return (
        !this.isDurationField && this.selectedTravelAttributeDetails.type === 'Number'
      );
    },
    valueAsDate() {
      if (this.conditions.comparison.value) {
        return moment(this.conditions.comparison.value).toDate();
      }

      return new Date();
    },
    getTravelAttributes() {
      return this.travelPolicyAttributes.filter(attr => {
        return attr.category === this.$helpers.capitalize(this.type);
      });
    },
  },
  mounted() {
    if (this.conditions.mode === 'edit') {
      this.isEdit = true;
    }
    if(this.selectedTravelAttributeDetails && this.selectedTravelAttributeDetails.type === 'Number'){
      this.conditions.comparison.value = parseInt(this.conditions.comparison.value)
    }
  },
  methods: {
    remove() {
      this.$emit('remove');
    },
    valueChanged(event) {
      const date = moment(event);
      this.conditions.comparison.value = date.format('YYYY-MM-DD');
    },
    handleSelectedAttributeChange() {
      this.tagAutocompleteKey++;
      this.allowComparison = true;
      this.conditions.comparison.value = '';
      if(this.selectedTravelAttributeDetails && this.selectedTravelAttributeDetails.type === 'Number'){
        this.conditions.comparison.value = parseInt(this.conditions.comparison.value)
      }
    },
    save() {
      const parts = [
        this.selectedTravelAttributeDetails.name,
        this.transformOperator,
        this.transformValue,
      ];

      this.conditions.message = parts.join(' ');
      this.conditions.attribute.id = this.selectedAttribute;
      this.conditions.attribute.name = this.selectedTravelAttributeDetails.name;
      this.conditions.mode = 'view';
      this.isEdit = false;
    },
    setComparisonValue(values) {
      this.conditions.comparison.value = values.map(value => value[0]).join(', ');
    },
  },
};
</script>

<style scoped>
.is-less-bottom {
  margin-bottom: -20px;
}

.container {
  margin-bottom: 5px;
  padding: 5px;
  border-style: groove;
}

.section-heading {
  font-size: 25px;
  margin-bottom: 10px;
  display: block;
  padding: 8px 8px;
  border-bottom: 2px solid;
  margin-bottom: 10px;
}
</style>
